/**
 * `compile(definition)` (1.K-1 deliverable 2). Never throws. The frozen `CalendarDefinition`
 * schema of `@codex/contracts` owns every per-field rule; each issue it raises becomes one
 * `invalid_shape` error at its path and `compile` stops. `compile` then adds only the cross-field
 * rules no schema field can state, and builds the frozen, branded `CompiledCalendar`.
 */
import { CalendarDefinition, isTick } from '@codex/contracts';
import { foldName, RESERVED_WORDS } from './fold.js';
import { add, floorMod, mul } from './math.js';
import { COMPILED_BRAND, } from './types.js';
import { daysBefore, monthLength, monthOffset, yearIsLeap } from './year.js';
/** Beyond 2^40 seconds a year, per-year arithmetic is no longer exact in this engine. */
const YEAR_SECONDS_LIMIT = 2 ** 40;
/** A JSON pointer (RFC 6901) from a schema issue's path. */
function pointer(path) {
    return path.map((seg) => `/${String(seg).replace(/~/g, '~0').replace(/\//g, '~1')}`).join('');
}
/**
 * Freezes what `compile` built, walking it without recursion: the calendar and every object
 * under it, `definition.source.extras` included, but not the values of `extras`. Those are the
 * caller's own (the schema passes them through by reference), may nest deeper than any stack, and
 * the engine never reads them.
 */
function freezeBuilt(calendar) {
    const extras = calendar.definition.source.extras;
    const pending = [calendar];
    for (let value = pending.pop(); value !== undefined; value = pending.pop()) {
        if (Object.isFrozen(value))
            continue;
        Object.freeze(value);
        if (value === extras)
            continue;
        for (const inner of Object.values(value)) {
            if (typeof inner === 'object' && inner !== null)
                pending.push(inner);
        }
    }
    return calendar;
}
class Issues {
    errors = [];
    warnings = [];
    error(code, path) {
        this.errors.push({ code, severity: 'error', path });
    }
    warn(code, path) {
        this.warnings.push({ code, severity: 'warning', path });
    }
}
export function compile(definition) {
    const parsed = CalendarDefinition.safeParse(definition);
    if (!parsed.success) {
        return {
            ok: false,
            errors: parsed.error.issues.map((issue) => ({
                code: 'invalid_shape',
                severity: 'error',
                path: pointer(issue.path),
            })),
            warnings: [],
        };
    }
    // The schema builds new objects for the definition, `source.extras` included (a Zod record is
    // rebuilt key by key), so freezing the compiled form never freezes a record the caller holds;
    // the values inside `extras` are the caller's, and `freezeBuilt` never enters them.
    const def = parsed.data;
    const issues = new Issues();
    // Months in ordinal order, declaration order between equal ordinals (Foundry's order).
    const order = def.months
        .map((_, i) => i)
        .sort((a, b) => ordinalOf(def, a) - ordinalOf(def, b) || a - b);
    checkMonths(def, order, issues);
    nameWarnings(def, issues);
    for (const [i, month] of def.months.entries()) {
        if (month.dayOffset !== null)
            issues.warn('day_offset_ignored', `/months/${i}/dayOffset`);
    }
    if (def.leap !== null && def.months.every((m) => m.leapDays === null)) {
        issues.warn('leap_rule_dormant', '/leap');
    }
    let commonDays = 0;
    let leapDays = 0;
    for (const month of def.months) {
        commonDays = add(commonDays, month.days);
        leapDays = add(leapDays, month.leapDays ?? month.days);
    }
    const secondsPerHour = mul(def.time.minutesPerHour, def.time.secondsPerMinute);
    const secondsPerDay = mul(def.time.hoursPerDay, secondsPerHour);
    const tooLarge = !(mul(commonDays, secondsPerDay) <= YEAR_SECONDS_LIMIT) ||
        (def.leap !== null && !(mul(leapDays, secondsPerDay) <= YEAR_SECONDS_LIMIT));
    if (tooLarge)
        issues.error('calendar_too_large', '');
    checkSeasons(def, leapDays, issues);
    if (issues.errors.length > 0)
        return { ok: false, errors: issues.errors, warnings: issues.warnings };
    const months = compileMonths(def, order);
    const model = {
        yearZero: def.yearZero,
        leap: def.leap,
        commonYearDays: commonDays,
        leapYearDays: def.leap === null ? commonDays : leapDays,
        months,
        secondsPerDay,
    };
    weekdayWarning(def, order, model, issues);
    const eras = compileEras(def, model, issues);
    if (eras === null)
        return { ok: false, errors: issues.errors, warnings: issues.warnings };
    const weekdays = def.weekdays
        .map((w, i) => ({ w, i }))
        .sort((a, b) => a.w.ordinal - b.w.ordinal || a.i - b.i)
        .map(({ w }) => ({ key: w.key, name: w.name }));
    const seasons = def.seasons
        .map((s) => ({ key: s.key, name: s.name, abbreviation: s.abbreviation, startDay: s.startDay }))
        .sort((a, b) => a.startDay - b.startDay);
    const calendar = {
        definition: def,
        yearZero: def.yearZero,
        hoursPerDay: def.time.hoursPerDay,
        minutesPerHour: def.time.minutesPerHour,
        secondsPerMinute: def.time.secondsPerMinute,
        secondsPerHour,
        secondsPerDay,
        leap: def.leap,
        commonYearDays: model.commonYearDays,
        leapYearDays: model.leapYearDays,
        months,
        regularMonths: months.filter((m) => !m.intercalary),
        weekdays,
        firstWeekday: def.firstWeekday,
        seasons,
        moons: def.moons.map((m) => ({ key: m.key, cycleLength: m.cycleLength, offset: m.offset })),
        eras: eras.list,
        erasByStart: eras.byStart,
        defaultEra: eras.defaultEra,
        approx: def.approx,
        worldTimeOffset: def.foundry.worldTimeOffset,
    };
    Object.defineProperty(calendar, COMPILED_BRAND, { value: true, enumerable: false });
    return { ok: true, calendar: freezeBuilt(calendar), warnings: issues.warnings };
}
function ordinalOf(def, i) {
    return def.months[i].ordinal;
}
function checkMonths(def, order, issues) {
    if (def.months.every((m) => m.intercalary === true))
        issues.error('no_regular_month', '/months');
    for (let k = 1; k < order.length; k++) {
        const i = order[k];
        if (ordinalOf(def, i) === ordinalOf(def, order[k - 1])) {
            issues.error('duplicate_ordinal', `/months/${i}/ordinal`);
        }
    }
    for (const [i, m] of def.months.entries()) {
        if (m.intercalary === true) {
            if (m.days === 0 && !(m.leapDays !== null && m.leapDays > 0)) {
                issues.error('bad_month_length', `/months/${i}/days`);
            }
        }
        else if (m.days === 0) {
            issues.error('bad_month_length', `/months/${i}/days`);
        }
        else if (m.leapDays === 0) {
            issues.error('bad_month_length', `/months/${i}/leapDays`);
        }
    }
}
/**
 * Two seasons may not share a start; and, so that every season begins in every year, no season
 * may start beyond the last day of a leap year that is shorter than a common year (a deviation
 * the 1.K-1 report records: the brief's table names only the shared start).
 */
function checkSeasons(def, leapDays, issues) {
    const seen = new Set();
    for (const [i, s] of def.seasons.entries()) {
        if (seen.has(s.startDay) || (def.leap !== null && s.startDay >= leapDays)) {
            issues.error('bad_season_start', `/seasons/${i}/startDay`);
        }
        seen.add(s.startDay);
    }
    for (let i = 1; i < def.seasons.length; i++) {
        const prev = def.seasons[i - 1];
        if (def.seasons[i].startDay < prev.startDay) {
            issues.warn('seasons_out_of_order', '/seasons');
            break;
        }
    }
}
function compileMonths(def, order) {
    const months = [];
    let commonOffset = 0;
    let leapOffset = 0;
    let regular = 0;
    for (const [index, i] of order.entries()) {
        const m = def.months[i];
        const intercalary = m.intercalary === true;
        const leapDays = m.leapDays ?? m.days;
        months.push({
            key: m.key,
            name: m.name,
            abbreviation: m.abbreviation,
            intercalary,
            index,
            regularIndex: intercalary ? -1 : regular++,
            commonDays: m.days,
            leapDays,
            commonOffset,
            leapOffset,
            startingWeekday: m.startingWeekday,
        });
        commonOffset += m.days;
        leapOffset += leapDays;
    }
    return months;
}
/**
 * `ambiguous_name`: two non-empty names, abbreviations or suffixes of months, seasons or eras
 * fold to the same text, or one folds to a word the parser reserves. One item's own name and
 * abbreviation (or suffix) may agree.
 */
function nameWarnings(def, issues) {
    const named = [];
    for (const [i, m] of def.months.entries()) {
        named.push({ owner: `m${i}`, path: `/months/${i}/name`, text: m.name });
        named.push({ owner: `m${i}`, path: `/months/${i}/abbreviation`, text: m.abbreviation });
    }
    for (const [i, s] of def.seasons.entries()) {
        named.push({ owner: `s${i}`, path: `/seasons/${i}/name`, text: s.name });
        named.push({ owner: `s${i}`, path: `/seasons/${i}/abbreviation`, text: s.abbreviation });
    }
    for (const [i, e] of def.eras.entries()) {
        named.push({ owner: `e${i}`, path: `/eras/${i}/name`, text: e.name });
        named.push({ owner: `e${i}`, path: `/eras/${i}/suffix`, text: e.suffix });
    }
    const firstOwner = new Map();
    for (const { owner, path, text } of named) {
        const folded = foldName(text ?? '');
        if (folded === '')
            continue;
        const first = firstOwner.get(folded);
        if (RESERVED_WORDS.has(folded) || (first !== undefined && first !== owner)) {
            issues.warn('ambiguous_name', path);
        }
        if (first === undefined)
            firstOwner.set(folded, owner);
    }
}
/**
 * `starting_weekday_inconsistent`: some month sets `startingWeekday`, and the two weekday formulas
 * can disagree on a date, because a year's length (common, or leap under a leap rule) is not a
 * whole number of weeks or a month's `startingWeekday` is not where the running count puts it
 * (P0-13's `isWeekdayConsistent`, extended to leap years).
 */
function weekdayWarning(def, order, model, issues) {
    const set = def.months.findIndex((m) => m.startingWeekday !== null);
    if (set < 0)
        return;
    const w = def.weekdays.length;
    let consistent = true;
    let firstBad = Number.POSITIVE_INFINITY;
    for (const leap of def.leap === null ? [false] : [false, true]) {
        if (floorMod(leap ? model.leapYearDays : model.commonYearDays, w) !== 0)
            consistent = false;
        for (const month of model.months) {
            if (month.startingWeekday === null || monthLength(month, leap) === 0)
                continue;
            const expected = floorMod(def.firstWeekday + monthOffset(month, leap), w);
            if (floorMod(month.startingWeekday, w) !== expected) {
                consistent = false;
                firstBad = Math.min(firstBad, order[month.index]);
            }
        }
    }
    if (!consistent) {
        const at = Number.isFinite(firstBad) ? firstBad : set;
        issues.warn('starting_weekday_inconsistent', `/months/${at}/startingWeekday`);
    }
}
/** The start tick of an era date, or undefined (with a `bad_era` error) when it cannot be placed. */
function dateTick(date, path, model, byKey, issues) {
    const month = byKey.get(date.month);
    if (month === undefined) {
        issues.error('bad_era', `${path}/month`);
        return undefined;
    }
    const leap = yearIsLeap(model, date.year);
    if (date.day > monthLength(month, leap)) {
        issues.error('bad_era', `${path}/day`);
        return undefined;
    }
    const days = add(daysBefore(model, date.year), monthOffset(month, leap) + date.day - 1);
    const tick = mul(days, model.secondsPerDay);
    if (!isTick(tick)) {
        issues.error('bad_era', path);
        return undefined;
    }
    return tick;
}
/** -1, 0 or 1 comparing two era starts, null (no start) being the earliest. */
function compareStarts(a, b) {
    if (a === b)
        return 0;
    if (a === null)
        return -1;
    if (b === null)
        return 1;
    return a < b ? -1 : 1;
}
function compileEras(def, model, issues) {
    const byKey = new Map(model.months.map((m) => [m.key, m]));
    const starts = [];
    const ends = [];
    for (const [i, era] of def.eras.entries()) {
        starts.push(era.start === null ? null : dateTick(era.start, `/eras/${i}/start`, model, byKey, issues));
        ends.push(era.end === null ? null : dateTick(era.end, `/eras/${i}/end`, model, byKey, issues));
    }
    for (const [i] of def.eras.entries()) {
        const start = starts[i];
        const end = ends[i];
        if (typeof start === 'number' && typeof end === 'number' && end <= start) {
            issues.error('bad_era', `/eras/${i}/end`);
        }
    }
    if (issues.errors.length > 0)
        return null;
    const startOf = (i) => starts[i];
    // The default era: the latest start (no start being the earliest), the first among equals.
    let defaultIndex = 0;
    for (let i = 1; i < def.eras.length; i++) {
        if (compareStarts(startOf(i), startOf(defaultIndex)) > 0)
            defaultIndex = i;
    }
    for (const [i, era] of def.eras.entries()) {
        if (i !== defaultIndex && era.name === '' && era.suffix === '') {
            issues.error('bad_era', `/eras/${i}`);
        }
    }
    if (issues.errors.length > 0)
        return null;
    const byStart = def.eras
        .map((_, i) => i)
        .sort((a, b) => compareStarts(startOf(a), startOf(b)) || a - b);
    const list = def.eras.map((era, i) => {
        let end = ends[i];
        if (end === null) {
            // Runs to the start of the first era, in start order, that starts later.
            const next = byStart.find((j) => compareStarts(startOf(j), startOf(i)) > 0);
            end = next === undefined ? null : startOf(next);
        }
        return {
            key: era.key,
            name: era.name,
            suffix: era.suffix,
            direction: era.direction,
            anchorYear: era.anchor.year,
            anchorEraYear: era.anchor.eraYear,
            start: startOf(i),
            end,
            index: i,
        };
    });
    eraWarnings(byStart, list, issues);
    return {
        list,
        byStart: byStart.map((i) => list[i]),
        defaultEra: list[defaultIndex],
    };
}
/** `eras_share_start` for each later era sharing a start; `eras_leave_gap` for each gap. */
function eraWarnings(byStart, list, issues) {
    for (let k = 1; k < byStart.length; k++) {
        const era = list[byStart[k]];
        const prev = list[byStart[k - 1]];
        if (compareStarts(era.start, prev.start) === 0) {
            issues.warn('eras_share_start', `/eras/${Math.max(era.index, prev.index)}/start`);
        }
    }
    const first = list[byStart[0]];
    if (first.start !== null)
        issues.warn('eras_leave_gap', `/eras/${first.index}/start`);
    // Sweep in start order: `covered` is where the eras seen so far stop (null: never).
    let covered = first.end;
    let coveredBy = first;
    for (const j of byStart.slice(1)) {
        const era = list[j];
        if (covered === null)
            return;
        if (era.start !== null && era.start > covered) {
            issues.warn('eras_leave_gap', `/eras/${coveredBy.index}/end`);
        }
        if (era.end === null || era.end > covered) {
            covered = era.end;
            coveredBy = era;
        }
    }
    if (covered !== null)
        issues.warn('eras_leave_gap', `/eras/${coveredBy.index}/end`);
}
//# sourceMappingURL=compile.js.map